import {
  addDays,
  addMonths,
  addWeeks,
  differenceInCalendarDays,
  differenceInCalendarWeeks,
  format,
  isAfter,
  isBefore,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
// UI
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  ArrowUpDown,
  Plus,
  Trash2,
  Users,
  Waves,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
// State
import { create } from "zustand";
import { persist } from "zustand/middleware";

// =====================
// Types
// =====================
export type Owner = {
  id: string;
  name: string;
  color: string;
  createdAt: string;
  updatedAt: string;
};
export type ProjectStatus = "confirmed" | "tentative";
export type ProjectType =
  | "AI Sprint"
  | "AI DaaS"
  | "R&D"
  | "Production"
  | "Support";
export type Priority = "high" | "med" | "low";
export type Effort = "high" | "med" | "low";
export type Project = {
  id: string;
  name: string;
  ownerId: string; // 'unassigned' or owner id
  startDate: string; // yyyy-mm-dd
  endDate: string; // yyyy-mm-dd
  status: ProjectStatus;
  ptype: ProjectType;
  priority: Priority;
  effort: Effort;
  completed: boolean;
  createdAt: string;
  updatedAt: string;
};

type Granularity = "week" | "biweek" | "month";
type Column = {
  start: Date;
  spanDays: number;
  month: string;
  subLabel: string;
};

// =====================
// Constants & Utils
// =====================
const OWNER_COLORS = [
  "#2563eb",
  "#059669",
  "#f59e0b",
  "#ef4444",
  "#7c3aed",
  "#0ea5e9",
  "#16a34a",
  "#f97316",
  "#db2777",
  "#14b8a6",
  "#4f46e5",
  "#a855f7",
];

const WEEK_STARTS_ON = 1; // Monday
const LEFT_RAIL_PX = 0; // left rail removed per new design
const ACTIONS_RAIL_PX = 0;
const PX_PER_DAY = 12; // constant pixels-per-day; horizontal scroll grows as the window widens

const clamp = (n: number, min: number, max: number) =>
  Math.max(min, Math.min(max, n));

function uuidv4() {
  return typeof crypto !== "undefined" && (crypto as any).getRandomValues
    ? (([1e7] as any) + -1e3 + -4e3 + -8e3 + -1e11).replace(
        /[018]/g,
        (c: any) =>
          (
            c ^
            ((crypto as any).getRandomValues(new Uint8Array(1))[0] &
              (15 >> (c / 4)))
          ).toString(16)
      )
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function weekOfMonthLabel(date: Date) {
  const monthStart = startOfMonth(date);
  const wInMonth =
    differenceInCalendarWeeks(date, monthStart, {
      weekStartsOn: WEEK_STARTS_ON,
    }) + 1;
  return `W${wInMonth}`;
}

function computeGranularity(zoomWeeks: number): Granularity {
  if (zoomWeeks >= 40) return "month";
  if (zoomWeeks >= 24) return "biweek";
  return "week";
}

function buildColumns(
  rangeStart: Date,
  rangeEnd: Date,
  granularity: Granularity
): Column[] {
  const cols: Column[] = [];
  if (granularity === "month") {
    let cursor = startOfMonth(rangeStart);
    while (!isAfter(cursor, rangeEnd)) {
      const next = addMonths(cursor, 1);
      const spanDays = differenceInCalendarDays(next, cursor);
      cols.push({
        start: cursor,
        spanDays,
        month: format(cursor, "MMM yyyy"),
        subLabel: "",
      });
      cursor = next;
    }
    return cols;
  }
  const stepWeeks = granularity === "biweek" ? 2 : 1;
  let cursor = startOfWeek(rangeStart, { weekStartsOn: WEEK_STARTS_ON });
  while (!isAfter(cursor, rangeEnd)) {
    const spanDays = 7 * stepWeeks;
    const wm = weekOfMonthLabel(cursor);
    const sub =
      granularity === "biweek"
        ? (() => {
            const n = parseInt(wm.slice(1), 10) || 1;
            return `W${n}${n + 1 <= 5 ? `–${n + 1}` : ""}`;
          })()
        : wm;
    cols.push({
      start: cursor,
      spanDays,
      month: format(cursor, "MMM yyyy"),
      subLabel: sub,
    });
    cursor = addWeeks(cursor, stepWeeks);
  }
  return cols;
}

function daysBetween(a: Date, b: Date) {
  return differenceInCalendarDays(startOfDay(b), startOfDay(a));
}
function snapToWeek(date: Date, toDaily = false) {
  return toDaily
    ? startOfDay(date)
    : startOfWeek(date, { weekStartsOn: WEEK_STARTS_ON });
}
function nextMondayLike(date: Date) {
  return startOfWeek(date, { weekStartsOn: WEEK_STARTS_ON });
}
function toLocalDate(dateStr: string) {
  return new Date(`${dateStr}T00:00:00`);
}
function toDateStr(d: Date) {
  return format(d, "yyyy-MM-dd");
}

// Color helpers
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const m = hex.trim().replace("#", "");
  if (m.length === 3) {
    const r = parseInt(m[0] + m[0], 16);
    const g = parseInt(m[1] + m[1], 16);
    const b = parseInt(m[2] + m[2], 16);
    return { r, g, b };
  }
  if (m.length === 6) {
    const r = parseInt(m.slice(0, 2), 16);
    const g = parseInt(m.slice(2, 4), 16);
    const b = parseInt(m.slice(4, 6), 16);
    return { r, g, b };
  }
  return null;
}
function rgbaString(
  rgb: { r: number; g: number; b: number } | null,
  alpha: number,
  fallback = "rgba(0,0,0,0.15)"
) {
  if (!rgb) return fallback;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

// =====================
// Supabase (HTTP only in canvas)
// =====================
const IS_CANVAS_SANDBOX =
  typeof window !== "undefined" &&
  typeof location !== "undefined" &&
  (location.href.startsWith("sandbox:") ||
    location.href.includes("chat.openai.com"));
const ENABLE_REALTIME = import.meta.env.VITE_ENABLE_REALTIME === "true";
// disable in canvas to avoid permission loop
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
let supabase: any = null;
async function getSupabase() {
  if (supabase) return supabase;
  const { createClient } = await import("@supabase/supabase-js");
  supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);
  return supabase;
}

let CLOUD_ON = false;
const NET_THROTTLE_MS = Number(import.meta.env.VITE_NET_THROTTLE_MS) || 150; // throttle network during drag
// throttle network during drag
const lastNetUpdate: Record<string, number> = {};
let rtChannel: any = null;

function handleOwnerChange(payload: any) {
  const ev = payload.eventType as "INSERT" | "UPDATE" | "DELETE";
  const row = payload.new || payload.old;
  if (!row) return;
  const now = new Date().toISOString();
  if (ev === "INSERT") {
    useStore.setState((s) =>
      s.owners.some((o) => o.id === row.id)
        ? s
        : {
            owners: [
              ...s.owners,
              {
                id: row.id,
                name: row.name,
                color: row.color,
                createdAt: now,
                updatedAt: now,
              },
            ],
          }
    );
  } else if (ev === "UPDATE") {
    useStore.setState((s) => ({
      owners: s.owners.map((o) =>
        o.id === row.id
          ? { ...o, name: row.name, color: row.color, updatedAt: now }
          : o
      ),
    }));
  } else if (ev === "DELETE") {
    useStore.setState((s) => ({
      owners: s.owners.filter((o) => o.id !== row.id),
    }));
  }
}

function handleProjectChange(payload: any) {
  const ev = payload.eventType as "INSERT" | "UPDATE" | "DELETE";
  const row = payload.new || payload.old;
  if (!row) return;
  const now = new Date().toISOString();
  if (ev === "INSERT") {
    useStore.setState((s) =>
      s.projects.some((p) => p.id === row.id)
        ? s
        : {
            projects: [
              ...s.projects,
              {
                id: row.id,
                name: row.name,
                ownerId: row.owner_id ?? "unassigned",
                startDate: row.start_date,
                endDate: row.end_date,
                status: row.status,
                ptype: row.ptype ?? "R&D",
                priority: row.priority ?? "med",
                effort: row.effort ?? "med",
                completed: row.completed ?? false,
                createdAt: now,
                updatedAt: now,
              },
            ],
          }
    );
  } else if (ev === "UPDATE") {
    useStore.setState((s) => ({
      projects: s.projects.map((p) =>
        p.id === row.id
          ? {
              ...p,
              name: row.name,
              ownerId: row.owner_id ?? "unassigned",
              startDate: row.start_date,
              endDate: row.end_date,
              status: row.status,
              ptype: row.ptype ?? "R&D",
              priority: row.priority ?? "med",
              effort: row.effort ?? "med",
              completed: row.completed ?? false,
              updatedAt: now,
            }
          : p
      ),
    }));
  } else if (ev === "DELETE") {
    useStore.setState((s) => ({
      projects: s.projects.filter((p) => p.id !== row.id),
    }));
  }
}

async function enableRealtime() {
  const sb = await getSupabase();
  if (rtChannel) return;
  rtChannel = sb
    .channel("planner-realtime")
    .on(
      "postgres_changes",
      { event: "*", schema: "public", table: "owners" } as any,
      handleOwnerChange
    )
    .on(
      "postgres_changes",
      { event: "*", schema: "public", table: "projects" } as any,
      handleProjectChange
    );
}

async function tryEnableRealtimeWithTimeout(
  _timeoutMs = 2500
): Promise<boolean> {
  // Canvas-safe no-op; return false so UI shows HTTP-only mode
  return false;
}

async function disableRealtime() {
  if (!rtChannel) return;
  try {
    await rtChannel.unsubscribe?.();
  } catch {}
  const sb = await getSupabase();
  try {
    sb.removeChannel?.(rtChannel);
  } catch {}
  rtChannel = null;
}

async function pullInitial() {
  const sb = await getSupabase();
  const { data: ownersData } = await sb.from("owners").select("*");
  const { data: projectsData } = await sb.from("projects").select("*");
  const now = new Date().toISOString();
  const owners: Owner[] = [
    {
      id: "unassigned",
      name: "Unassigned",
      color: "#9ca3af",
      createdAt: now,
      updatedAt: now,
    },
    ...(ownersData || []).map((r: any) => ({
      id: r.id,
      name: r.name,
      color: r.color,
      createdAt: now,
      updatedAt: now,
    })),
  ];
  const projects: Project[] = (projectsData || []).map((r: any) => ({
    id: r.id,
    name: r.name,
    ownerId: r.owner_id ?? "unassigned",
    startDate: r.start_date,
    endDate: r.end_date,
    status: r.status,
    ptype: r.ptype ?? "R&D",
    priority: r.priority ?? "med",
    effort: r.effort ?? "med",
    completed: r.completed ?? false,
    createdAt: now,
    updatedAt: now,
  }));
  useStore.setState((s) => {
    // Merge remote with local by id so local edits aren't blown away if a server update failed
    const localById = new Map(s.projects.map((p) => [p.id, p]));
    const merged: Project[] = [];
    for (const r of projects) {
      const local = localById.get(r.id);
      merged.push(local ? { ...r, ...local } : r);
      if (local) localById.delete(r.id);
    }
    // include any purely local items not on the server
    for (const [, leftover] of localById) merged.push(leftover);
    return { owners, projects: merged };
  });
}

async function seedCloudFromLocalIfEmpty() {
  const sb = await getSupabase();
  const { data: oRow } = await sb.from("owners").select("id").limit(1);
  const { data: pRow } = await sb.from("projects").select("id").limit(1);
  if ((oRow?.length ?? 0) === 0 && (pRow?.length ?? 0) === 0) {
    const { owners, projects } = useStore.getState();
    const ownersRows = owners
      .filter((o) => o.id !== "unassigned")
      .map((o) => ({ id: o.id, name: o.name, color: o.color }));
    if (ownersRows.length) await sb.from("owners").insert(ownersRows);
    if (projects.length)
      await sb.from("projects").insert(
        projects.map((p) => ({
          id: p.id,
          name: p.name,
          owner_id: p.ownerId === "unassigned" ? null : p.ownerId,
          start_date: p.startDate,
          end_date: p.endDate,
          status: p.status,
          ptype: p.ptype,
          priority: p.priority,
          effort: p.effort,
        }))
      );
  }
}

// =====================
// Store (Zustand + persist)
// =====================

type SortKey =
  | "owner"
  | "start"
  | "end"
  | "type"
  | "length"
  | "status"
  | "priority"
  | "effort";
type SortDir = "asc" | "desc";

type Store = {
  owners: Owner[];
  projects: Project[];
  filters: {
    showTentative: boolean;
    hideCompleted: boolean;
    ownerIds: string[];
    ptypes: ProjectType[];
    priorities: Priority[];
    efforts: Effort[];
    sortKey: SortKey;
    sortDir: SortDir;
    query: string;
  };
  zoomWeeks: number;
  scrollLeftPct: number; // 0..1 percentage across the scrollable width
  addOwner: (name: string) => void;
  updateOwner: (id: string, patch: Partial<Owner>) => void;
  deleteOwner: (id: string) => void;
  addProject: (p: Omit<Project, "id" | "createdAt" | "updatedAt">) => void;
  updateProject: (id: string, patch: Partial<Project>) => void;
  deleteProject: (id: string) => void;
  setFilters: (patch: Partial<Store["filters"]>) => void;
  setZoomWeeks: (v: number) => void;
  setScrollLeftPct: (v: number) => void;
};

export const useStore = create<Store>()(
  persist(
    (set, get) => ({
      owners: [
        {
          id: "unassigned",
          name: "Unassigned",
          color: "#9ca3af",
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
      ],
      projects: [],
      filters: {
        showTentative: true,
        hideCompleted: false,
        ownerIds: [],
        ptypes: [],
        priorities: [],
        efforts: [],
        sortKey: "owner",
        sortDir: "asc",
        query: "",
      },
      zoomWeeks: 24,
      scrollLeftPct: 0,

      addOwner: (name) => {
        const existing = get().owners;
        const used = new Set(existing.map((o) => o.color));
        const color =
          OWNER_COLORS.find((c) => !used.has(c)) ||
          OWNER_COLORS[Math.floor(Math.random() * OWNER_COLORS.length)];
        const now = new Date().toISOString();
        const id = uuidv4();
        set({
          owners: [
            ...existing,
            { id, name, color, createdAt: now, updatedAt: now },
          ],
        });
        if (CLOUD_ON)
          getSupabase()
            .then((sb) => sb.from("owners").insert({ id, name, color }))
            .catch(console.error);
      },
      updateOwner: (id, patch) => {
        set({
          owners: get().owners.map((o) =>
            o.id === id
              ? { ...o, ...patch, updatedAt: new Date().toISOString() }
              : o
          ),
        });
        if (CLOUD_ON) {
          const upd: any = {};
          if (patch.name !== undefined) upd.name = patch.name;
          if (patch.color !== undefined) upd.color = patch.color;
          if (Object.keys(upd).length)
            getSupabase()
              .then((sb) => sb.from("owners").update(upd).eq("id", id))
              .catch(console.error);
        }
      },
      deleteOwner: (id) => {
        if (id === "unassigned") return;
        const owners = get().owners.filter((o) => o.id !== id);
        const projects = get().projects.map((p) =>
          p.ownerId === id ? { ...p, ownerId: "unassigned" } : p
        );
        set({ owners, projects });
        if (CLOUD_ON)
          getSupabase()
            .then((sb) => sb.from("owners").delete().eq("id", id))
            .catch(console.error);
      },

      addProject: (p) => {
        const now = new Date().toISOString();
        const id = uuidv4();
        set({
          projects: [
            ...get().projects,
            { ...p, id, createdAt: now, updatedAt: now },
          ],
        });
        if (CLOUD_ON) {
          const row = {
            id,
            name: p.name,
            owner_id: p.ownerId === "unassigned" ? null : p.ownerId,
            start_date: p.startDate,
            end_date: p.endDate,
            status: p.status,
            ptype: p.ptype,
            priority: p.priority,
            effort: p.effort,
          };
          getSupabase()
            .then(async (sb) => {
              const { error } = await sb.from("projects").insert(row);
              if (error)
                console.error(
                  "Supabase insert failed:",
                  error.message || error
                );
            })
            .catch(console.error);
        }
      },
      updateProject: (id, patch) => {
        set({
          projects: get().projects.map((p) =>
            p.id === id
              ? { ...p, ...patch, updatedAt: new Date().toISOString() }
              : p
          ),
        });
        if (CLOUD_ON) {
          const now = Date.now();
          if ((lastNetUpdate[id] || 0) + NET_THROTTLE_MS <= now) {
            lastNetUpdate[id] = now;
            const upd: any = {};
            if (patch.name !== undefined) upd.name = patch.name;
            if (patch.ownerId !== undefined)
              upd.owner_id =
                patch.ownerId === "unassigned" ? null : patch.ownerId;
            if (patch.startDate !== undefined) upd.start_date = patch.startDate;
            if (patch.endDate !== undefined) upd.end_date = patch.endDate;
            if (patch.status !== undefined) upd.status = patch.status;
            if (patch.ptype !== undefined) upd.ptype = patch.ptype;
            if (patch.priority !== undefined) upd.priority = patch.priority;
            if (patch.effort !== undefined) upd.effort = patch.effort;
            if (patch.completed !== undefined) upd.completed = patch.completed;
            if (Object.keys(upd).length)
              getSupabase()
                .then(async (sb) => {
                  const { error } = await sb
                    .from("projects")
                    .update(upd)
                    .eq("id", id);
                  if (error) {
                    console.error(
                      "Supabase update failed:",
                      error.message || error
                    );
                    console.error("Update data:", upd);
                  }
                })
                .catch(console.error);
          }
        }
      },
      deleteProject: (id) => {
        set({ projects: get().projects.filter((p) => p.id !== id) });
        if (CLOUD_ON)
          getSupabase()
            .then((sb) => sb.from("projects").delete().eq("id", id))
            .catch(console.error);
      },

      setFilters: (patch) => set({ filters: { ...get().filters, ...patch } }),
      setZoomWeeks: (v) => set({ zoomWeeks: clamp(v, 8, 52) }),
      setScrollLeftPct: (v) => set({ scrollLeftPct: clamp(v, 0, 1) }),
    }),
    { name: "planner-v2-new-ui", version: 1 }
  )
);

// =====================
// Owners (used inside the sheet)
// =====================
function OwnersPanel() {
  const owners = useStore((s) => s.owners);
  const addOwner = useStore((s) => s.addOwner);
  const updateOwner = useStore((s) => s.updateOwner);
  const deleteOwner = useStore((s) => s.deleteOwner);
  const [name, setName] = useState("");

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const n = name.trim();
    if (!n) return;
    addOwner(n);
    setName("");
  };

  return (
    <div className="space-y-4">
      <form className="flex gap-2" onSubmit={submit}>
        <Input
          placeholder="Add owner"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Button type="submit">
          <Plus className="w-4 h-4 mr-1" />
          Add
        </Button>
      </form>
      <div className="divide-y">
        {owners.map((o) => (
          <div key={o.id} className="flex items-center gap-2 py-2">
            <div className="w-4 h-4 rounded" style={{ background: o.color }} />
            <Input
              className="h-8 w-56"
              defaultValue={o.name}
              onBlur={(e) => updateOwner(o.id, { name: e.target.value })}
            />
            <Input
              className="h-8 w-24"
              type="color"
              defaultValue={o.color}
              onChange={(e) => updateOwner(o.id, { color: e.target.value })}
            />
            <div className="flex-1" />
            {o.id !== "unassigned" && (
              <Button
                size="icon"
                variant="ghost"
                onClick={() => deleteOwner(o.id)}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// =====================
// Project Form
// =====================
function ProjectForm({
  onClose,
  existing,
}: {
  onClose: () => void;
  existing?: Project;
}) {
  const owners = useStore((s) => s.owners);
  const addProject = useStore((s) => s.addProject);
  const updateProject = useStore((s) => s.updateProject);
  const deleteProject = useStore((s) => s.deleteProject);

  const [name, setName] = useState(existing?.name || "");
  const [ownerId, setOwnerId] = useState(
    existing?.ownerId || owners[0]?.id || "unassigned"
  );
  const [status, setStatus] = useState(existing?.status || "confirmed");
  const [ptype, setPType] = useState(existing?.ptype || "R&D");
  const [priority, setPriority] = useState(existing?.priority || "med");
  const [effort, setEffort] = useState(existing?.effort || "med");
  const [completed, setCompleted] = useState(!!existing?.completed);

  const startSeed = existing
    ? toLocalDate(existing.startDate)
    : nextMondayLike(new Date());
  const endSeed = existing
    ? toLocalDate(existing.endDate)
    : addWeeks(startSeed, 2);

  const [start, setStart] = useState(format(startSeed, "yyyy-MM-dd"));
  const [end, setEnd] = useState(format(endSeed, "yyyy-MM-dd"));

  function save() {
    const payload = {
      name: name.trim(),
      ownerId,
      status,
      ptype,
      priority,
      effort,
      startDate: start,
      endDate: end,
      completed: existing ? completed : false,
    } as const;
    if (!payload.name) return;
    if (existing) updateProject(existing.id, payload as any);
    else addProject(payload as any);
    onClose();
  }

  return (
    <div className="grid grid-cols-6 gap-3">
      <div className="col-span-3">
        <Label>Name</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="col-span-3">
        <Label>Owner</Label>
        <Select
          value={ownerId || "unassigned"}
          onValueChange={(v) => setOwnerId(v)}
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {owners.map((o) => (
              <SelectItem key={o.id} value={o.id}>
                {o.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Status</Label>
        <Select
          value={status}
          onValueChange={(v: ProjectStatus) => setStatus(v)}
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="tentative">Tentative</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Type</Label>
        <Select value={ptype} onValueChange={(v: ProjectType) => setPType(v)}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="AI Sprint">AI Sprint</SelectItem>
            <SelectItem value="AI DaaS">AI DaaS</SelectItem>
            <SelectItem value="R&D">R&D</SelectItem>
            <SelectItem value="Production">Production</SelectItem>
            <SelectItem value="Support">Support</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Priority</Label>
        <Select
          value={priority}
          onValueChange={(v: Priority) => setPriority(v)}
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="med">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Effort</Label>
        <Select value={effort} onValueChange={(v: Effort) => setEffort(v)}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="med">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Start</Label>
        <Input
          type="date"
          value={start}
          onChange={(e) => setStart(e.target.value)}
        />
      </div>
      <div>
        <Label>End</Label>
        <Input
          type="date"
          value={end}
          onChange={(e) => setEnd(e.target.value)}
        />
      </div>
      {existing && (
        <div className="col-span-2 flex items-center gap-2">
          <Checkbox
            id="completed"
            checked={completed}
            onCheckedChange={(v) => setCompleted(!!v)}
          />
          <Label htmlFor="completed">Completed</Label>
        </div>
      )}
      <div className="col-span-6 flex items-center justify-between gap-2 mt-2">
        {existing && (
          <Button
            variant="destructive"
            onClick={() => {
              deleteProject(existing.id);
              onClose();
            }}
          >
            Delete
          </Button>
        )}
        <div className="ml-auto flex gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save}>Save</Button>
        </div>
      </div>
    </div>
  );
}

// =====================
// Header timeline (months only + controls)
// =====================
function TimelineHeader({
  columns,
  pxPerDay,
  zoomWeeks,
  onZoom,
  centerWidth,
}: {
  columns: Column[];
  pxPerDay: number;
  zoomWeeks: number;
  onZoom: (v: number) => void;
  centerWidth: number;
}) {
  const monthSpans: { month: string; count: number }[] = [];
  columns.forEach((c) => {
    const last = monthSpans[monthSpans.length - 1];
    if (!last || last.month !== c.month)
      monthSpans.push({ month: c.month, count: 1 });
    else last.count += 1;
  });
  const colsTemplate = columns
    .map((c) => `${c.spanDays * pxPerDay}px`)
    .join(" ");
  const today = startOfDay(new Date());
  const headerStart = columns[0]?.start || today;
  const todayLeft = daysBetween(headerStart, today) * pxPerDay;

  return (
    <div className="w-full">
      {/* Sticky month header inside scroll area */}
      <div className="sticky top-0 z-20 bg-white">
        <div className="flex">
          <div style={{ width: LEFT_RAIL_PX }} />
          <div
            style={{ width: centerWidth }}
            className="overflow-visible relative"
          >
            <div className="grid" style={{ gridTemplateColumns: colsTemplate }}>
              {monthSpans.map((m, i) => (
                <div
                  key={i}
                  className="border-l text-center py-1 text-[11px] text-muted-foreground uppercase tracking-wider"
                  style={{ gridColumn: `span ${m.count}` }}
                >
                  {m.month}
                </div>
              ))}
            </div>
          </div>
          <div style={{ width: ACTIONS_RAIL_PX }} />
        </div>
      </div>
    </div>
  );
}

// =====================
// Row rendering
// =====================
function ProjectRow({
  project,
  rangeStart,
  rangeEnd,
  gridStart,
  pxPerDay,
  centerWidth,
  columns,
  onDragStateChange,
}: {
  project: Project;
  rangeStart: Date;
  rangeEnd: Date;
  gridStart: Date;
  pxPerDay: number;
  centerWidth: number;
  columns: Column[];
  onDragStateChange: (projectId: string, isDragging: boolean) => void;
}) {
  const updateProject = useStore((s) => s.updateProject);
  const deleteProject = useStore((s) => s.deleteProject);
  const owners = useStore((s) => s.owners);

  const owner = owners.find((o) => o.id === project.ownerId) || owners[0];

  // derive tinted colors from owner
  const ownerRGB = hexToRgb(owner.color);
  const borderCol = rgbaString(ownerRGB, 0.5, "rgba(0,0,0,0.2)"); // 50% toward white on white bg
  const shadowCol = rgbaString(ownerRGB, 0.25, "rgba(0,0,0,0.15)"); // subtle, tinted shadow

  const [drag, setDrag] = useState<any>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [tooltip, setTooltip] = useState<null | { left: number; text: string }>(
    null
  );
  const [isDragging, setIsDragging] = useState(false);

  // Notify parent component of drag state changes
  useEffect(() => {
    onDragStateChange(project.id, isDragging);
  }, [isDragging, onDragStateChange, project.id]);

  const start = toLocalDate(project.startDate);
  const end = toLocalDate(project.endDate);

  const GAP_BETWEEN = 15;
  const barH =
    project.effort === "high" ? 60 : project.effort === "low" ? 30 : 45; // explicit heights
  const rowH = barH + GAP_BETWEEN; // ensures 15px space between rows
  const topPx = Math.floor(GAP_BETWEEN / 2);

  const initials = (owner.name || "")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  const priorityClass =
    project.priority === "high"
      ? "bg-red-100 text-red-700"
      : project.priority === "med"
      ? "bg-amber-100 text-amber-700"
      : "bg-green-100 text-green-700";

  const left = Math.max(0, daysBetween(gridStart, start) * pxPerDay);
  const width = Math.max(pxPerDay, (daysBetween(start, end) + 1) * pxPerDay);

  const weeksCount = Math.max(
    1,
    differenceInCalendarWeeks(rangeEnd, gridStart, {
      weekStartsOn: WEEK_STARTS_ON,
    }) + 1
  );
  const weekPx = pxPerDay * 7;
  const CLICK_MOVE_THRESHOLD = 3;
  const todayLeft = daysBetween(gridStart, startOfDay(new Date()));

  function onPointerDown(
    e: React.PointerEvent,
    mode: "move" | "left" | "right"
  ) {
    (e.target as Element).setPointerCapture?.((e as any).pointerId);
    setDrag({
      mode,
      originX: e.clientX,
      originY: e.clientY,
      startOrig: start,
      endOrig: end,
      moved: false,
    });
    setIsDragging(true);
  }
  function onPointerMove(e: React.PointerEvent) {
    if (!drag) return;
    const dx = e.clientX - drag.originX;
    const dy = e.clientY - (drag.originY ?? e.clientY);
    if (
      !drag.moved &&
      (Math.abs(dx) >= CLICK_MOVE_THRESHOLD ||
        Math.abs(dy) >= CLICK_MOVE_THRESHOLD)
    ) {
      setDrag({ ...drag, moved: true });
    }
    const daysFloat = dx / pxPerDay;
    const dayDelta = Math.round(daysFloat);
    const daily = e.altKey;
    let newStart = drag.startOrig;
    let newEnd = drag.endOrig;
    if (drag.mode === "move") {
      newStart = snapToWeek(addDays(drag.startOrig, dayDelta), daily);
      newEnd = snapToWeek(addDays(drag.endOrig, dayDelta), daily);
    } else if (drag.mode === "left") {
      newStart = snapToWeek(addDays(drag.startOrig, dayDelta), daily);
      if (isAfter(newStart, newEnd)) newStart = newEnd;
    } else {
      newEnd = snapToWeek(addDays(drag.endOrig, dayDelta), daily);
      if (isBefore(newEnd, newStart)) newEnd = newStart;
    }
    const sISO = toDateStr(newStart);
    const eISO = toDateStr(newEnd);
    if (drag.lastAppliedStart !== sISO || drag.lastAppliedEnd !== eISO) {
      updateProject(project.id, { startDate: sISO, endDate: eISO });
      setDrag({ ...drag, lastAppliedStart: sISO, lastAppliedEnd: eISO });
      setTooltip({
        left: daysBetween(rangeStart, newStart) * pxPerDay,
        text: `${format(newStart, "EEE, MMM d, yyyy")} → ${format(
          newEnd,
          "EEE, MMM d, yyyy"
        )}`,
      });
    }
  }
  function onPointerUp(e: React.PointerEvent) {
    if (drag) {
      (e.target as Element).releasePointerCapture?.((e as any).pointerId);
      if (!drag.moved && drag.mode === "move") {
        setShowDialog(true);
      }
    }
    setDrag(null);
    setTooltip(null);
    setIsDragging(false);
  }

  return (
    <div style={{ height: rowH }}>
      <div
        className="relative select-none"
        style={{ width: centerWidth }}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
        {/* weekly vertical grid lines */}
        <div className="absolute inset-0 pointer-events-none z-0">
          <div
            className="grid h-full"
            style={{
              gridTemplateColumns: `repeat(${weeksCount}, ${weekPx}px)`,
            }}
          >
            {Array.from({ length: weeksCount }).map((_, i) => (
              <div key={i} className="border-l border-neutral-200/70" />
            ))}
          </div>
        </div>
        {/* bar */}
        <div
          className={`absolute z-20 rounded-md border bg-white flex items-center cursor-grab ${
            project.status === "tentative" ? "opacity-50 border-dashed" : ""
          } ${project.completed ? "opacity-20" : ""}`}
          style={{
            left,
            width,
            top: topPx,
            height: barH,
            borderColor: borderCol,
            boxShadow: `0 1px 4px ${shadowCol}`,
          }}
          onPointerDown={(e) => onPointerDown(e, "move")}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
        >
          <div
            className="absolute left-0 top-0 bottom-0 w-1 rounded-l-md"
            style={{ background: owner.color }}
          />
          <div className="pl-3 pr-2 flex items-center gap-2 text-[11px] text-neutral-700 overflow-hidden">
            <div
              className="w-5 h-5 rounded-full text-white flex items-center justify-center text-[10px]"
              style={{ background: owner.color }}
            >
              {initials || "?"}
            </div>
            <span className="font-medium text-[12px] truncate max-w-[240px]">
              {project.name}
            </span>
            <span className="px-1.5 py-0.5 rounded border bg-neutral-50 whitespace-nowrap">
              {project.ptype}
            </span>
            <span
              className={`px-1.5 py-0.5 rounded-full capitalize whitespace-nowrap ${priorityClass}`}
            >
              {project.priority}
            </span>
            {project.status === "tentative" && (
              <span className="inline-block w-2 h-2 rounded-full bg-yellow-400" />
            )}
          </div>
          <div className="flex-1" />
          {/* resize handles */}
          <div
            className="w-2 h-full cursor-ew-resize opacity-0 hover:opacity-100"
            onPointerDown={(e) => {
              e.stopPropagation();
              onPointerDown(e, "left");
            }}
          />
          <div
            className="w-2 h-full cursor-ew-resize opacity-0 hover:opacity-100"
            onPointerDown={(e) => {
              e.stopPropagation();
              onPointerDown(e, "right");
            }}
          />
        </div>

        {tooltip && (
          <div
            className="absolute -top-6 z-30 px-2 py-0.5 text-[11px] rounded bg-black text-white"
            style={{ left: tooltip.left }}
          >
            {tooltip.text}
          </div>
        )}
      </div>

      {/* Edit dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[680px]">
          <DialogHeader>
            <DialogTitle>Edit Project</DialogTitle>
          </DialogHeader>
          <ProjectForm
            onClose={() => setShowDialog(false)}
            existing={project}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =====================
// Main App
// ===================== (New UI)
// =====================
export default function App() {
  const today = new Date();

  const [showNewProject, setShowNewProject] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dbError, setDbError] = useState<string | null>(null);
  const [isAnyProjectDragging, setIsAnyProjectDragging] = useState(false);
  const [activeDragIds, setActiveDragIds] = useState<Set<string>>(new Set());
  const [frozenOrder, setFrozenOrder] = useState<string[] | null>(null);

  // data
  const filters = useStore((s) => s.filters);
  const setFilters = useStore((s) => s.setFilters);
  const ownersList = useStore((s) => s.owners);
  const projects = useStore((s) => s.projects);
  const scrollLeftPct = useStore((s) => s.scrollLeftPct);
  const setScrollLeftPct = useStore((s) => s.setScrollLeftPct);

  // Ensure persisted state has rehydrated before attempting initial scroll restore
  const [hasHydrated, setHasHydrated] = useState<boolean>(false);
  useEffect(() => {
    const persistApi = (useStore as any)?.persist;
    const unsubStart = persistApi?.onHydrate?.(() => setHasHydrated(false));
    const unsubFinish = persistApi?.onFinishHydration?.(() =>
      setHasHydrated(true)
    );
    setHasHydrated(persistApi?.hasHydrated?.() || false);
    return () => {
      unsubStart?.();
      unsubFinish?.();
    };
  }, []);

  // zoom controls (how many weeks approx visible) - persisted in store
  const zoomWeeks = useStore((s) => s.zoomWeeks);
  const setZoomWeeks = useStore((s) => s.setZoomWeeks);
  const granularity = useMemo(() => computeGranularity(zoomWeeks), [zoomWeeks]);

  // zoom to center functionality
  const [lastZoomCenter, setLastZoomCenter] = useState<number | null>(null);

  const handleZoomChange = (newZoomWeeks: number) => {
    if (!scrollRef.current) {
      setZoomWeeks(newZoomWeeks);
      return;
    }

    // Calculate the center point of the current viewport
    const scrollContainer = scrollRef.current;
    const centerX =
      scrollContainer.scrollLeft + scrollContainer.clientWidth / 2;

    // Calculate what percentage of the total timeline width is at the center
    const totalWidth = centerWidth;
    const centerPercentage = centerX / totalWidth;

    // Store the center percentage for the next render
    setLastZoomCenter(centerPercentage);

    // Update zoom
    setZoomWeeks(newZoomWeeks);
  };

  // measure viewport width to derive px-per-day from zoom
  const scrollRef = useRef<HTMLDivElement>(null);
  const didInitialScroll = useRef(false);
  const rafScrollRef = useRef<number | null>(null);
  const [viewportWidth, setViewportWidth] = useState(1200);
  useEffect(() => {
    const update = () =>
      setViewportWidth(scrollRef.current?.clientWidth || window.innerWidth);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const pxPerDay = useMemo(() => {
    const pd = viewportWidth / (zoomWeeks * 7);
    return clamp(pd, 4, 28);
  }, [viewportWidth, zoomWeeks]);

  // dynamic bounds: whole timeline from month before earliest start to month after latest end
  const { rangeStart, rangeEnd } = useMemo(() => {
    const list = projects;
    if (!list.length) {
      const rs = startOfMonth(addMonths(today, -1));
      const re = startOfMonth(addMonths(today, 1));
      return { rangeStart: rs, rangeEnd: re };
    }
    let minStart = toLocalDate(list[0].startDate);
    let maxEnd = toLocalDate(list[0].endDate);
    for (const p of list) {
      const s = toLocalDate(p.startDate);
      const e = toLocalDate(p.endDate);
      if (s < minStart) minStart = s;
      if (e > maxEnd) maxEnd = e;
    }
    return {
      rangeStart: startOfMonth(addMonths(minStart, -1)),
      rangeEnd: startOfMonth(addMonths(maxEnd, 1)),
    };
  }, [projects, today]);

  const columns = useMemo(
    () => buildColumns(rangeStart, rangeEnd, granularity),
    [rangeStart, rangeEnd, granularity]
  );
  const centerWidth = useMemo(
    () => columns.reduce((sum, c) => sum + c.spanDays * pxPerDay, 0),
    [columns, pxPerDay]
  );

  // Supabase bootstrap
  useEffect(() => {
    (async () => {
      try {
        await pullInitial();
        CLOUD_ON = true;
        if (ENABLE_REALTIME && !IS_CANVAS_SANDBOX) {
          await tryEnableRealtimeWithTimeout(2500);
        }
        seedCloudFromLocalIfEmpty().catch(() => {});
      } catch (e: any) {
        CLOUD_ON = false;
        setDbError(e?.message || null);
      }
    })();
    return () => {
      disableRealtime();
    };
  }, []);

  // Filtering / sorting
  const ownerIds = filters.ownerIds ?? [];
  const ptypes = filters.ptypes ?? [];
  const priorities = filters.priorities ?? [];
  const efforts = filters.efforts ?? [];

  const ownersMap = useMemo(
    () => new Map(ownersList.map((o) => [o.id, o.name])),
    [ownersList]
  );
  const rank = (v: any) => (v === "high" ? 3 : v === "med" ? 2 : 1);
  const baseFiltered = useMemo(() => {
    let list = projects.filter(
      (p) => filters.showTentative || p.status !== "tentative"
    );
    if (filters.hideCompleted) {
      list = list.filter((p) => !p.completed);
    }
    if ((filters.query || "").trim()) {
      const q = (filters.query || "").toLowerCase();
      list = list.filter((p) => p.name.toLowerCase().includes(q));
    }
    if (ownerIds.length)
      list = list.filter((p) => ownerIds.includes(p.ownerId || ""));
    if (ptypes.length) list = list.filter((p) => ptypes.includes(p.ptype));
    if (priorities.length)
      list = list.filter((p) => priorities.includes(p.priority));
    if (efforts.length) list = list.filter((p) => efforts.includes(p.effort));

    const dir = filters.sortDir === "asc" ? 1 : -1;
    list.sort((a, b) => {
      switch (filters.sortKey) {
        case "owner":
          return (
            (ownersMap.get(a.ownerId || "") || "").localeCompare(
              ownersMap.get(b.ownerId || "") || ""
            ) * dir
          );
        case "start":
          return (
            (toLocalDate(a.startDate).getTime() -
              toLocalDate(b.startDate).getTime()) *
            dir
          );
        case "end":
          return (
            (toLocalDate(a.endDate).getTime() -
              toLocalDate(b.endDate).getTime()) *
            dir
          );
        case "type":
          return a.ptype.localeCompare(b.ptype) * dir;
        case "length":
          return (
            (differenceInCalendarDays(
              toLocalDate(a.endDate),
              toLocalDate(a.startDate)
            ) -
              differenceInCalendarDays(
                toLocalDate(b.endDate),
                toLocalDate(b.startDate)
              )) *
            dir
          );
        case "status":
          return (
            ((a.status === "tentative" ? 1 : 0) -
              (b.status === "tentative" ? 1 : 0)) *
            dir
          );
        case "priority":
          return (rank(a.priority) - rank(b.priority)) * dir;
        case "effort":
          return (rank(a.effort) - rank(b.effort)) * dir;
        default:
          return 0;
      }
    });

    return list;
  }, [projects, filters, ownersMap]);

  // Freeze only the order (by IDs) during drag; keep data live
  const filtered = useMemo(() => {
    if (!isAnyProjectDragging || !frozenOrder) return baseFiltered;
    const frozenIndex = new Map(frozenOrder.map((id, i) => [id, i] as const));
    const withIndex = baseFiltered.map((p, baseIndex) => ({
      p,
      frozenPos: frozenIndex.get(p.id),
      baseIndex,
    }));
    // Stable: first by frozen position, then by the original base order index
    withIndex.sort((a, b) => {
      const ai = a.frozenPos ?? Number.MAX_SAFE_INTEGER;
      const bi = b.frozenPos ?? Number.MAX_SAFE_INTEGER;
      if (ai !== bi) return ai - bi;
      return a.baseIndex - b.baseIndex;
    });
    return withIndex.map((x) => x.p);
  }, [isAnyProjectDragging, frozenOrder, baseFiltered]);

  const colsTemplate = useMemo(
    () => columns.map((c) => `${c.spanDays * pxPerDay}px`).join(" "),
    [columns, pxPerDay]
  );
  const gridStart = columns[0]?.start || rangeStart;
  const todayLineLeft =
    daysBetween(gridStart, startOfDay(new Date())) * pxPerDay;

  // initial horizontal scroll restore from persisted percentage (after store hydration)
  useEffect(() => {
    if (
      !scrollRef.current ||
      didInitialScroll.current ||
      columns.length === 0 ||
      !hasHydrated
    )
      return;
    const totalWidth = centerWidth;
    const container = scrollRef.current;
    const maxScrollable = Math.max(0, totalWidth - container.clientWidth);
    const targetLeft = Math.max(
      0,
      Math.min(
        maxScrollable,
        scrollLeftPct * totalWidth - container.clientWidth / 2
      )
    );
    try {
      container.scrollTo({ left: targetLeft, top: 0, behavior: "auto" });
    } catch {
      (container as any).scrollLeft = targetLeft;
    }
    didInitialScroll.current = true;
  }, [columns, pxPerDay, centerWidth, scrollLeftPct, hasHydrated]);

  // Persist scroll on user scroll (throttled by rAF)
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    const onScroll = () => {
      if (rafScrollRef.current != null) return;
      rafScrollRef.current = requestAnimationFrame(() => {
        rafScrollRef.current = null;
        const totalWidth = centerWidth;
        const centerX = container.scrollLeft + container.clientWidth / 2;
        const pct = totalWidth > 0 ? centerX / totalWidth : 0;
        setScrollLeftPct(clamp(pct, 0, 1));
      });
    };
    container.addEventListener("scroll", onScroll, { passive: true } as any);
    return () => {
      container.removeEventListener("scroll", onScroll as any);
      if (rafScrollRef.current != null)
        cancelAnimationFrame(rafScrollRef.current);
      rafScrollRef.current = null;
    };
  }, [centerWidth, setScrollLeftPct]);

  // Effect to handle scroll adjustment after zoom change
  useEffect(() => {
    if (lastZoomCenter !== null && scrollRef.current) {
      const newPxPerDay = clamp(viewportWidth / (zoomWeeks * 7), 4, 28);
      const newTotalWidth = columns.reduce(
        (sum, c) => sum + c.spanDays * newPxPerDay,
        0
      );
      const newCenterX = lastZoomCenter * newTotalWidth;
      const newScrollLeft = newCenterX - scrollRef.current.clientWidth / 2;

      scrollRef.current.scrollTo({
        left: Math.max(0, newScrollLeft),
        behavior: "auto",
      });

      setLastZoomCenter(null);
    }
  }, [zoomWeeks, lastZoomCenter, columns, viewportWidth]);

  const monthLines = useMemo(() => {
    const arr: number[] = [];
    if (!columns.length) return arr;
    let acc = 0;
    let prev = columns[0].month;
    for (let i = 0; i < columns.length; i++) {
      const c = columns[i];
      if (i > 0 && c.month !== prev) arr.push(acc);
      acc += c.spanDays * pxPerDay;
      prev = c.month;
    }
    return arr;
  }, [columns, pxPerDay]);

  // Callback to handle drag state changes from project rows
  const handleDragStateChange = useCallback(
    (projectId: string, isDragging: boolean) => {
      setActiveDragIds((prev) => {
        const next = new Set(prev);
        if (isDragging) {
          const wasEmpty = next.size === 0;
          next.add(projectId);
          if (wasEmpty) {
            setFrozenOrder(baseFiltered.map((p) => p.id));
            setIsAnyProjectDragging(true);
          }
        } else {
          next.delete(projectId);
          if (next.size === 0) {
            setFrozenOrder(null);
            setIsAnyProjectDragging(false);
          }
        }
        return next;
      });
    },
    [baseFiltered]
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Top Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur">
        <div className="h-14 px-4 grid grid-cols-3 items-center">
          {/* Left: Logo */}
          <div className="flex items-center gap-2 justify-self-start">
            <Waves className="w-5 h-5" />
            <span className="font-semibold">Flow</span>
          </div>

          {/* Center: Search, Filters & Sorting */}
          <div className="flex items-center gap-2 justify-self-center text-xs flex-nowrap whitespace-nowrap overflow-x-auto">
            {/* Quick Search */}
            <div className="relative">
              <Input
                placeholder="Search projects..."
                className="h-8 w-52 pr-6"
                value={filters.query || ""}
                onChange={(e) => setFilters({ query: e.target.value })}
              />
              {filters.query && (
                <button
                  aria-label="Clear search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                  onClick={() => setFilters({ query: "" })}
                >
                  ×
                </button>
              )}
            </div>
            {/* Owner filter */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs shrink-0"
                >
                  {ownerIds.length
                    ? `${ownerIds.length} selected`
                    : "All owners"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="p-1 text-xs max-h-64 overflow-y-auto"
              >
                <DropdownMenuCheckboxItem
                  checked={ownerIds.length === 0}
                  onCheckedChange={() => setFilters({ ownerIds: [] })}
                >
                  All owners
                </DropdownMenuCheckboxItem>
                {ownersList.map((o) => (
                  <DropdownMenuCheckboxItem
                    key={o.id}
                    checked={ownerIds.includes(o.id)}
                    onCheckedChange={(c) => {
                      const s = new Set(ownerIds);
                      if (c) s.add(o.id);
                      else s.delete(o.id);
                      setFilters({ ownerIds: Array.from(s) });
                    }}
                  >
                    {o.name}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            {/* Type */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs shrink-0"
                >
                  {ptypes.length ? `${ptypes.length} types` : "All types"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="p-1 text-xs">
                <DropdownMenuCheckboxItem
                  checked={ptypes.length === 0}
                  onCheckedChange={() => setFilters({ ptypes: [] })}
                >
                  All types
                </DropdownMenuCheckboxItem>
                {["AI Sprint", "AI DaaS", "R&D", "Production", "Support"].map(
                  (t) => (
                    <DropdownMenuCheckboxItem
                      key={t}
                      checked={ptypes.includes(t as any)}
                      onCheckedChange={(c) => {
                        const s = new Set(ptypes);
                        if (c) s.add(t as any);
                        else s.delete(t as any);
                        setFilters({ ptypes: Array.from(s) as any });
                      }}
                      className="capitalize"
                    >
                      {t}
                    </DropdownMenuCheckboxItem>
                  )
                )}
                <div className="h-px bg-border my-1" />
                <DropdownMenuCheckboxItem
                  checked={filters.hideCompleted}
                  onCheckedChange={(c) => setFilters({ hideCompleted: !!c })}
                >
                  Hide completed
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={!filters.showTentative}
                  onCheckedChange={(c) => setFilters({ showTentative: !c })}
                >
                  Hide tentative
                </DropdownMenuCheckboxItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Priority */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs shrink-0"
                >
                  {priorities.length
                    ? `${priorities.length} priority`
                    : "All priorities"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="p-1 text-xs">
                <DropdownMenuCheckboxItem
                  checked={priorities.length === 0}
                  onCheckedChange={() => setFilters({ priorities: [] })}
                >
                  All priorities
                </DropdownMenuCheckboxItem>
                {["high", "med", "low"].map((t) => (
                  <DropdownMenuCheckboxItem
                    key={t}
                    checked={priorities.includes(t as any)}
                    onCheckedChange={(c) => {
                      const s = new Set(priorities);
                      if (c) s.add(t as any);
                      else s.delete(t as any);
                      setFilters({ priorities: Array.from(s) as any });
                    }}
                    className="capitalize"
                  >
                    {t}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Effort */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs shrink-0"
                >
                  {efforts.length ? `${efforts.length} effort` : "All effort"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="p-1 text-xs">
                <DropdownMenuCheckboxItem
                  checked={efforts.length === 0}
                  onCheckedChange={() => setFilters({ efforts: [] })}
                >
                  All effort
                </DropdownMenuCheckboxItem>
                {["high", "med", "low"].map((t) => (
                  <DropdownMenuCheckboxItem
                    key={t}
                    checked={efforts.includes(t as any)}
                    onCheckedChange={(c) => {
                      const s = new Set(efforts);
                      if (c) s.add(t as any);
                      else s.delete(t as any);
                      setFilters({ efforts: Array.from(s) as any });
                    }}
                    className="capitalize"
                  >
                    {t}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Sort */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs shrink-0 flex items-center gap-1"
                >
                  <ArrowUpDown className="w-3 h-3" />
                  Sort:{" "}
                  {filters.sortKey === "owner"
                    ? "Owner"
                    : filters.sortKey === "start"
                    ? "Start Date"
                    : filters.sortKey === "end"
                    ? "End Date"
                    : filters.sortKey === "type"
                    ? "Type"
                    : filters.sortKey === "length"
                    ? "Length"
                    : filters.sortKey === "status"
                    ? "Status"
                    : filters.sortKey === "priority"
                    ? "Priority"
                    : filters.sortKey === "effort"
                    ? "Effort"
                    : "Owner"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="p-1 text-xs">
                <DropdownMenuCheckboxItem
                  checked={filters.sortKey === "owner"}
                  onCheckedChange={() => setFilters({ sortKey: "owner" })}
                >
                  Owner
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={filters.sortKey === "start"}
                  onCheckedChange={() => setFilters({ sortKey: "start" })}
                >
                  Start Date
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={filters.sortKey === "end"}
                  onCheckedChange={() => setFilters({ sortKey: "end" })}
                >
                  End Date
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={filters.sortKey === "type"}
                  onCheckedChange={() => setFilters({ sortKey: "type" })}
                >
                  Type
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={filters.sortKey === "length"}
                  onCheckedChange={() => setFilters({ sortKey: "length" })}
                >
                  Length
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={filters.sortKey === "status"}
                  onCheckedChange={() => setFilters({ sortKey: "status" })}
                >
                  Status
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={filters.sortKey === "priority"}
                  onCheckedChange={() => setFilters({ sortKey: "priority" })}
                >
                  Priority
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={filters.sortKey === "effort"}
                  onCheckedChange={() => setFilters({ sortKey: "effort" })}
                >
                  Effort
                </DropdownMenuCheckboxItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Sort Direction */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs shrink-0"
                >
                  {filters.sortDir === "asc" ? "Asc" : "Desc"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="p-1 text-xs">
                <DropdownMenuCheckboxItem
                  checked={filters.sortDir === "asc"}
                  onCheckedChange={() => setFilters({ sortDir: "asc" })}
                >
                  Ascending
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={filters.sortDir === "desc"}
                  onCheckedChange={() => setFilters({ sortDir: "desc" })}
                >
                  Descending
                </DropdownMenuCheckboxItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Right: Actions */}
          <div className="justify-self-end flex items-center gap-2">
            {/* Zoom Control */}
            <div className="flex items-center gap-2 text-xs mr-2">
              <ZoomIn className="w-4 h-4 text-muted-foreground" />
              <input
                type="range"
                min="8"
                max="52"
                value={zoomWeeks}
                onChange={(e) => handleZoomChange(parseInt(e.target.value))}
                className="w-20 h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
              />
              <ZoomOut className="w-4 h-4 text-muted-foreground" />
            </div>

            <Button size="sm" onClick={() => setShowNewProject(true)}>
              <Plus className="w-4 h-4 mr-1" />
              New Project
            </Button>
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Users className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-96">
                <SheetHeader>
                  <SheetTitle>Owners</SheetTitle>
                </SheetHeader>
                <OwnersPanel />
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Timeline & rows */}
      <div ref={scrollRef} className="relative overflow-auto">
        <div className="relative" style={{ width: centerWidth }}>
          <TimelineHeader
            columns={columns}
            pxPerDay={pxPerDay}
            zoomWeeks={zoomWeeks}
            onZoom={setZoomWeeks}
            centerWidth={centerWidth}
          />
          <div className="relative">
            {/* Month boundary lines */}
            {monthLines.map((x, i) => (
              <div
                key={i}
                className="pointer-events-none absolute top-0 bottom-0 w-px bg-neutral-200"
                style={{ left: x }}
              />
            ))}
            {/* Today line */}
            <div
              className="pointer-events-none absolute top-0 bottom-0 w-px bg-red-500/90"
              style={{ left: todayLineLeft }}
            />
            {/* Rows */}
            {filtered.map((p) => (
              <ProjectRow
                key={p.id}
                project={p}
                rangeStart={rangeStart}
                rangeEnd={rangeEnd}
                gridStart={gridStart}
                pxPerDay={pxPerDay}
                centerWidth={centerWidth}
                columns={columns}
                onDragStateChange={handleDragStateChange}
              />
            ))}
          </div>
        </div>
      </div>

      {/* New Project Dialog */}
      <Dialog open={showNewProject} onOpenChange={setShowNewProject}>
        <DialogContent className="sm:max-w-[680px]">
          <DialogHeader>
            <DialogTitle>New Project</DialogTitle>
          </DialogHeader>
          <ProjectForm onClose={() => setShowNewProject(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
